import { Flex, Group } from '@mantine/core';
import { Button, IconButton, Text } from '@repo/foundations';

import { trackEvent } from '@repo/common/analytics';
import { noop } from 'lodash-es';
import { useAuthUser } from '../../../api';
import type { AutomationJob } from '../../../api/types/models/automation/automationJob';
import { ANALYTICS_EVENTS } from '../../../utils/constants/analytics';
import { formatEntityDateTime } from '../../../utils/time';

interface AutomationJobActionBarProps {
	automationJob: AutomationJob;
	onClose: () => void;
	handleCompleteAutomationReview: (automationJobId: string) => void;
	isCompleteAutomationReviewLoading: boolean;
	includeCloseButton?: boolean;
	hideStartedTime?: boolean;
}

export function AutomationJobActionBar({
	automationJob,
	onClose,
	handleCompleteAutomationReview,
	isCompleteAutomationReviewLoading,
	includeCloseButton = true,
	hideStartedTime = false,
}: AutomationJobActionBarProps) {
	const { user, workspace } = useAuthUser();

	const showUpdateAndApproveAllButtons = automationJob.status === 'pending';
	const showRevertButton = automationJob.status === 'success';

	const handleApproveAllUpdates = () => {
		trackEvent(
			ANALYTICS_EVENTS.AI_OUTPUT_APPROVE_ALL,
			{
				automation_id: automationJob.automation?.toString() || '',
				job_id: automationJob.id,
				affected_records_count:
					automationJob.affected_records_count?.toString() || '0',
			},
			user,
			workspace
		);
		handleCompleteAutomationReview(automationJob.id);
	};

	return (
		<Flex w="100%" justify="space-between" align="center" p="sm" px="xl">
			<Text size="sm" fw={500}>
				{!hideStartedTime && formatEntityDateTime(automationJob.started_at)}
			</Text>
			<Group spacing={4} ml="sm">
				{showUpdateAndApproveAllButtons && (
					<Button
						variant="primary"
						onClick={handleApproveAllUpdates}
						disabled={isCompleteAutomationReviewLoading}
					>
						Approve all updates
					</Button>
				)}
				{showRevertButton && (
					<Button variant="default" onClick={noop}>
						Revert
					</Button>
				)}
				{includeCloseButton && (
					<IconButton
						variant="tertiary"
						iconName="x"
						size="md"
						tooltip="Close"
						onClick={onClose}
					/>
				)}
			</Group>
		</Flex>
	);
}
